/**
 * Focus Preview Pane
 * Middle pane displaying the selected issue with a tabbed interface
 * Tabs: Overview, Fix Guide, Videos, Automation, History
 */

import { useEffect, useState } from 'react'
import {
  makeStyles,
  tokens,
  Spinner,
  Button,
  TabList,
  Tab,
  SelectTabEvent,
  SelectTabData,
} from '@fluentui/react-components'
import {
  ArrowClockwiseRegular,
  HomeRegular,
  BookOpenRegular,
  VideoRegular,
  PlayCircleRegular,
  HistoryRegular,
} from '@fluentui/react-icons'
import type { FocusPreviewTabValue } from '../../../domain'
import { useIssueDetails } from '../../../data'
import { EmptyStateMessage } from './EmptyStateMessage'
import { FocusPreviewHeader } from './FocusPreviewHeader'
import {
  OverviewTabContent,
  FixGuideTabContent,
  VideosTabContent,
  AutomationTabContent,
  HistoryTabContent,
} from './tabs'
import { usePaneStyles } from '../styles/usePaneStyles'

const useStyles = makeStyles({
  tabListContainer: {
    // Match header background for unified surface
    backgroundColor: tokens.colorBrandBackground2,
    // No shadow - unified surface
    boxShadow: 'none',
    // No top border - continuity with header
    borderTop: 'none',
    // Bottom border separates tabs from content
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    // Tighter vertical padding
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
  },
  tabContent: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
})

export interface FocusPreviewPaneProps {
  selectedIssueId: string | null
}

export function FocusPreviewPane({ selectedIssueId }: FocusPreviewPaneProps) {
  const paneStyles = usePaneStyles()
  const styles = useStyles()
  const [selectedTab, setSelectedTab] = useState<FocusPreviewTabValue>('overview')

  const {
    data: issueDetails,
    isLoading: loading,
    error,
    refetch: handleRetry,
  } = useIssueDetails(selectedIssueId)

  // Reset to overview tab when selecting a new issue
  useEffect(() => {
    if (selectedIssueId) {
      setSelectedTab('overview')
    }
  }, [selectedIssueId])

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as FocusPreviewTabValue)
  }

  // No selection state
  if (!selectedIssueId) {
    return (
      <div className={paneStyles.root} role="region" aria-label="Focus Preview">
        <EmptyStateMessage
          title="Select an issue"
          description="Choose an issue from the work queue to view details"
        />
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className={paneStyles.root} role="region" aria-label="Focus Preview">
        <div className={paneStyles.loadingContainer}>
          <Spinner size="medium" label="Loading issue details..." />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={paneStyles.root} role="region" aria-label="Focus Preview">
        <EmptyStateMessage
          title="Failed to load issue"
          description="Something went wrong while loading this issue. Please try again."
          action={
            <Button
              icon={<ArrowClockwiseRegular />}
              appearance="primary"
              onClick={() => handleRetry()}
            >
              Retry
            </Button>
          }
        />
      </div>
    )
  }

  // Issue not found state
  if (!issueDetails) {
    return (
      <div className={paneStyles.root} role="region" aria-label="Focus Preview">
        <EmptyStateMessage
          title="Issue type not onboarded"
          description="Please contact autopilot@microsoft.com to onboard this issue type."
        />
      </div>
    )
  }

  // Render tab content based on selection
  const renderTabContent = () => {
    switch (selectedTab) {
      case 'overview':
        return <OverviewTabContent issue={issueDetails} />
      case 'fix-guide':
        return <FixGuideTabContent issue={issueDetails} />
      case 'videos':
        return <VideosTabContent issue={issueDetails} />
      case 'automation':
        return <AutomationTabContent issue={issueDetails} />
      case 'history':
        return <HistoryTabContent issue={issueDetails} />
      default:
        return <OverviewTabContent issue={issueDetails} />
    }
  }

  // Ready state with tabs
  return (
    <div className={paneStyles.root} role="region" aria-label="Focus Preview">
      <FocusPreviewHeader issue={issueDetails} />

      <div className={styles.tabListContainer}>
        <TabList
          selectedValue={selectedTab}
          onTabSelect={handleTabSelect}
          size="small"
        >
          <Tab value="overview" icon={<HomeRegular />}>
            Overview
          </Tab>
          <Tab value="fix-guide" icon={<BookOpenRegular />}>
            Fix Guide
          </Tab>
          <Tab value="videos" icon={<VideoRegular />}>
            Videos
          </Tab>
          <Tab value="automation" icon={<PlayCircleRegular />}>
            Automation
          </Tab>
          <Tab value="history" icon={<HistoryRegular />}>
            History
          </Tab>
        </TabList>
      </div>

      <div className={styles.tabContent}>{renderTabContent()}</div>
    </div>
  )
}
